// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// App shell: top navigation, role-filtered side navigation, and flash messages (requirements 1.5, 1.8, 9.2).
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import AppLayout from '@cloudscape-design/components/app-layout';
import Flashbar from '@cloudscape-design/components/flashbar';
import SideNavigation, { type SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import TopNavigation, { type TopNavigationProps } from '@cloudscape-design/components/top-navigation';
import { canAuthor, hasRole, useAuth } from '../auth/AuthProvider';
import { setsEnabled } from '../config';
import { setColorMode, useColorMode } from '../lib/colorMode';
import { useNotify } from '../lib/notify';

/**
 * Side navigation by role. `sets`: blueprint sets are enabled in this deployment (`config.json` `sets.enabled`);
 * without it no set navigation is shown (blueprint-sets Req 1.1, 12.3, 14.2).
 */
export function navItems(roles: {
  author: boolean;
  viewer: boolean;
  admin: boolean;
  sets?: boolean;
}): SideNavigationProps.Item[] {
  const items: SideNavigationProps.Item[] = [];
  if (roles.author) {
    items.push({ type: 'link', text: 'Blueprints', href: '/blueprints' });
    if (roles.sets) items.push({ type: 'link', text: 'Blueprint sets', href: '/sets' });
    items.push(
      { type: 'link', text: 'Run a document', href: '/run' },
      { type: 'link', text: 'History', href: '/history' },
      { type: 'link', text: 'My handoff packages', href: '/packages' },
    );
  }
  items.push({ type: 'link', text: 'Shared with me', href: '/shared' });
  if (roles.admin) {
    items.push(
      { type: 'divider' },
      {
        type: 'section',
        text: 'Admin',
        items: [
          { type: 'link', text: 'Usage', href: '/admin' },
          { type: 'link', text: 'Users', href: '/admin/users' },
          { type: 'link', text: 'All blueprints', href: '/admin/blueprints' },
        ],
      },
    );
  }
  return items;
}

/** Accessible name of the dark mode toggle; its state is aria-pressed. */
export const DARK_MODE_LABEL = 'Dark mode';

// Decorative (the button is named by its text); stroke follows the button's text color in both modes.
const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  focusable: false,
  'aria-hidden': true,
} as const;
const MoonIcon = () => (
  <svg {...iconProps}>
    <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7Z" strokeLinejoin="round" />
  </svg>
);

export function Layout() {
  const { user, signOut } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const dark = useColorMode() === 'dark';
  const topNav = useRef<HTMLDivElement>(null);
  // TopNavigation's button utility has no pressed-state prop, so the toggle's state is set on its element here. It
  // renders (and re-renders, as the viewport changes) its utilities after measuring them, hence the observer.
  useEffect(() => {
    const root = topNav.current;
    if (!root) return;
    const mark = () =>
      root.querySelectorAll(`[aria-label="${DARK_MODE_LABEL}"]`).forEach((b) => {
        if (b.getAttribute('aria-pressed') !== String(dark)) b.setAttribute('aria-pressed', String(dark));
      });
    mark();
    const observer = new MutationObserver(mark);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [dark]);
  const items = navItems({
    author: canAuthor(user),
    viewer: hasRole(user, 'BlueprintViewer'),
    admin: hasRole(user, 'Admin'),
    sets: setsEnabled(),
  });
  const follow = (e: CustomEvent<{ href: string; external?: boolean }>) => {
    if (e.detail.external) return;
    e.preventDefault();
    navigate(e.detail.href);
  };
  const utilities: TopNavigationProps.Utility[] = [
    {
      type: 'button',
      text: DARK_MODE_LABEL,
      ariaLabel: DARK_MODE_LABEL,
      iconSvg: <MoonIcon />,
      // Kept out of the narrow-screen overflow menu, which would drop the pressed state.
      disableUtilityCollapse: true,
      onClick: () => setColorMode(dark ? 'light' : 'dark'),
    },
  ];
  if (user) {
    utilities.push({
      type: 'menu-dropdown',
      text: user.name || user.email,
      description: user.email,
      iconName: 'user-profile',
      items: [{ id: 'signout', text: 'Sign out' }],
      onItemClick: (e) => {
        if (e.detail.id === 'signout') void signOut();
      },
    });
  }
  return (
    <>
      <div id="top-nav" ref={topNav} style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{
            href: '/',
            title: 'BDA Blueprint Studio',
            onFollow: (e) => {
              e.preventDefault();
              navigate('/');
            },
          }}
          utilities={utilities}
        />
      </div>
      <AppLayout
        headerSelector="#top-nav"
        toolsHide
        navigation={<SideNavigation activeHref={activeHref(location.pathname)} items={items} onFollow={follow} />}
        notifications={<Flashbar items={notify.items} stackItems={notify.items.length > 3} />}
        content={<Outlet />}
        ariaLabels={{
          navigation: 'Main navigation',
          navigationClose: 'Close navigation',
          navigationToggle: 'Open navigation',
          notifications: 'Notifications',
        }}
      />
    </>
  );
}

export function activeHref(path: string): string {
  for (const p of [
    '/admin/users',
    '/admin/blueprints',
    '/admin',
    '/blueprints',
    '/sets',
    '/run',
    '/history',
    '/packages',
    '/shared',
  ]) {
    if (path === p || path.startsWith(`${p}/`)) return p;
  }
  return path;
}
